import { gql, useApolloClient, useLazyQuery, useQuery } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { useCallback } from 'react';

import { MeasureBundleObject, NudgeObject } from '@grow-therapy-team/seedling-components';

import { Server } from '../apollo/types';
import { logger } from '../datadog/logger';
import { NoteTypeEnum } from './client-information/types';
import {
  isSelectedClientUserMinorAtom,
  isSelectedClientUserUnderSafetyGuardAgeAtom,
  isSelectedPatientEnrolledInStructuredCareProgram,
  selectedClientUserShortIdAtom,
  selectedPatientId,
  selectedPatientPreferredNameAtom,
  selectedVisitorNotesAtomData,
} from './state';
import { getIsClientUserUnderSafetyGuardAge } from './utils';

export type GetPatientByIdQueryData = {
  patientChart?: {
    __typename?: 'PatientObject';
    id: string;
    shortId: string;
    name: string;
    firstName?: string | null;
    lastName?: string | null;
    preferredFullName?: string | null;
    preferredShortName?: string | null;
    pronouns?: string | null;
    archived: boolean;
    archivedReason?: string | null;
    email?: string | null;
    telephone?: string | null;
    gender?: string | null;
    readableDob?: string | null;
    age?: number | null;
    isSelfPay: boolean;
    formattedAddress?: string | null;
    customerId: string;
    status: string;
    seeingProviderInOffice: boolean;
    reasonForTherapy?: string | null;
    hasCompletedMeasureResponses?: boolean;
    isEnrolledInStructuredCareProgram?: boolean;
    primaryInsuranceCoverage?: {
      __typename?: 'InsuranceCoverageObject';
      uuid: unknown;
      groupNumber?: string | null;
      memberId: string;
      payorClientBookingName: {
        __typename?: 'PayorClientBookingNameObject';
        name: string;
      };
    } | null;
    diagnoses: Array<{
      __typename?: 'DiagnosisObject';
      diagnosis: string;
      icdCode: string;
    }>;
    provider?: { __typename?: 'PublicProviderObject'; id: string } | null;
    nextAppointment?: {
      __typename?: 'AppointmentObject';
      id: string;
      timeStart: unknown;
    } | null;
    lastAppointment?: {
      __typename?: 'AppointmentObject';
      id: string;
      timeStart: unknown;
      timeEnd: unknown;
      notes: Array<{
        __typename?: 'NoteObject';
        id: number;
        noteType: NoteTypeEnum;
        noteSummary?: string | null;
        noteContent?: string | null;
      }>;
    } | null;
    clientUser?: {
      __typename?: 'ClientUserObject';
      clientUserShortId: string;
      gender?: string | null;
      stateOfResidence?: string | null;
      isMinor?: boolean;
      streamUserId?: string | null;
    } | null;
    lastSubmittedClaim?: {
      __typename?: 'ClaimObject';
    } | null;
    measureBundles: MeasureBundleObject[];
    nudges: NudgeObject[] | null;
  } | null;
};

export type GetPatientByShortIdVariables = {
  patientShortId?: string;
};

export const GET_PATIENT_BY_PATIENT_SHORT_ID_GQL = gql`
  query GetPatientByPatientId($patientShortId: ID!) {
    patientChart(values: { patientShortId: $patientShortId }) {
      id
      shortId
      name
      firstName
      lastName
      preferredFullName
      preferredShortName
      pronouns
      archived
      archivedReason
      isEnrolledInStructuredCareProgram
      email
      telephone
      gender
      readableDob
      age
      isSelfPay
      primaryInsuranceCoverage {
        uuid
        payorClientBookingName {
          name
        }
        groupNumber
        memberId
      }
      formattedAddress
      customerId
      status
      seeingProviderInOffice
      reasonForTherapy
      hasCompletedMeasureResponses
      diagnoses {
        diagnosis
        icdCode
      }
      provider {
        id
        shortId
      }
      nextAppointment {
        id
        timeStart
      }
      lastAppointment {
        id
        timeStart
        timeEnd
        notes {
          id
          noteType
          noteSummary
          noteContent
        }
      }
      clientUser {
        clientUserShortId
        isMinor
        gender
        stateOfResidence
        streamUserId
      }
      lastSubmittedClaim {
        sessionDate
      }
    }
  }
`;

export function useSetPatientDataCallback() {
  const setNotes = useSetAtom(selectedVisitorNotesAtomData);
  const setClientUserShortId = useSetAtom(selectedClientUserShortIdAtom);
  const setPreferredPatientName = useSetAtom(selectedPatientPreferredNameAtom);
  const setIsPatientEnrolledInStructuredCareProgram = useSetAtom(isSelectedPatientEnrolledInStructuredCareProgram);
  const setPatientId = useSetAtom(selectedPatientId);

  const setIsClientUserMinor = useSetAtom(isSelectedClientUserMinorAtom);
  const setIsClientUserUnderSafetyGuardAge = useSetAtom(isSelectedClientUserUnderSafetyGuardAgeAtom);

  return useCallback(
    (data: GetPatientByIdQueryData) => {
      const patientChart = data?.patientChart;
      if (!patientChart) return;
      setClientUserShortId(patientChart?.clientUser?.clientUserShortId);
      setPreferredPatientName({
        patientFirstName: patientChart?.preferredShortName,
        patientLastName: patientChart?.lastName,
      });
      setPatientId(patientChart.id);
      setIsPatientEnrolledInStructuredCareProgram(patientChart?.isEnrolledInStructuredCareProgram);
      setIsClientUserMinor(patientChart?.clientUser?.isMinor ?? false);
      setIsClientUserUnderSafetyGuardAge(getIsClientUserUnderSafetyGuardAge(patientChart?.readableDob));

      if (patientChart?.lastAppointment?.notes && patientChart.lastAppointment.notes.length) {
        setNotes(
          patientChart.lastAppointment.notes[0]?.noteContent as string,
          parseInt(patientChart.id),
          patientChart.lastAppointment.notes[0].noteType,
        );
      }
    },
    [
      setNotes,
      setPatientId,
      setPreferredPatientName,
      setClientUserShortId,
      setIsPatientEnrolledInStructuredCareProgram,
      setIsClientUserMinor,
      setIsClientUserUnderSafetyGuardAge,
    ],
  );
}

export function useGetPatientByShortId(patientShortId?: string) {
  const setPatientData = useSetPatientDataCallback();
  return useQuery<GetPatientByIdQueryData, GetPatientByShortIdVariables>(GET_PATIENT_BY_PATIENT_SHORT_ID_GQL, {
    variables: { patientShortId },
    skip: !patientShortId,
    context: { server: Server.MONOLITH },
    onError: (error) => {
      logger.error('Unable to fetch client information', { patientShortId }, error);
    },
    onCompleted: (data) => {
      setPatientData(data);
    },
  });
}

export function useLazyGetPatientByShortId(patientShortId?: string) {
  const setPatientData = useSetPatientDataCallback();
  return useLazyQuery<GetPatientByIdQueryData, GetPatientByShortIdVariables>(GET_PATIENT_BY_PATIENT_SHORT_ID_GQL, {
    variables: { patientShortId },
    context: { server: Server.MONOLITH },
    onError: (error) => {
      logger.error('Unable to fetch client information', { patientShortId }, error);
    },
    onCompleted: (data) => {
      setPatientData(data);
    },
  });
}

export function useGetCachedPatientByShortIdCallback() {
  const apolloClient = useApolloClient();
  return useCallback(
    (variables: GetPatientByShortIdVariables) => {
      try {
        return apolloClient.readQuery<GetPatientByIdQueryData>({
          query: GET_PATIENT_BY_PATIENT_SHORT_ID_GQL,
          variables,
        })?.patientChart;
      } catch (error) {
        logger.error('Unable to read current provider from cache', {}, error as Error);
      }
    },
    [apolloClient],
  );
}
